// Armar un outfit a mano.
//
// La pantalla de sugerencias resuelve "no sé qué ponerme". Esta resuelve
// lo contrario: ya sabés exactamente qué te pusiste y solo querés
// registrarlo.
//
// LA REGLA DE ESTA PANTALLA ES QUE NO HAY REGLAS. Ni color, ni
// formalidad, ni vetos, ni estación, ni zonas. Si te pusiste una remera
// con una camisa abierta encima, son dos prendas de torso y la app te
// deja: vos sabés qué te pusiste.
//
// ─────────────────────────────────────────────────────────────────
// LO QUE ESTABA MAL Y SE CORRIGIÓ
//
// La versión anterior agrupaba las prendas por zona —Arriba, Abajo,
// Calzado— y al elegir una segunda camisa sacaba la primera. Copié esa
// regla del motor de combinaciones sin pensar si correspondía.
//
// No correspondía. El motor exige "cada zona exactamente una vez"
// porque ENUMERA combinaciones, y sin esa restricción el espacio de
// búsqueda explota. Esta pantalla no enumera nada: registra lo que la
// persona efectivamente usó. Y superponer prendas es normal — remera
// con camisa encima, chaleco sobre camisa, dos pares de medias.
//
// Se fue el reemplazo, se fueron los grupos por zona y se fue el
// "falta cubrir". Queda: tocar suma, tocar de nuevo saca.
//
// Para encontrar entre 55 prendas quedan tres ayudas que ACOTAN LO QUE
// SE VE sin limitar lo que se puede elegir: repetir un outfit anterior,
// filtrar por tipo, y buscar por nombre.
// ─────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as api from "./api";
import Visor from "./Visor";
import { hoyLocal, hace, aISO, diaLocalDe } from "./fechas";
import {
  alternarSeleccion,
  prendasQueSiguenVivas,
  filtrarPrendas,
} from "./seleccion";
import { fotoChica, fotoGrande } from "./foto";
import { t, tCat, tError, useIdioma, fecha as fechaEnIdioma, numero } from "./i18n";
import { cargarOpciones } from "./opciones";
import FrascoBoton from "./FrascoBoton";
import { perfumeSugerido } from "./perfumesDatos";

export default function Armador({ alGuardar, alEditar, alCancelarEdicion, semilla, alUsarSemilla }) {
  // Re-renderiza si cambia el idioma.
  useIdioma();
  const [prendas, setPrendas] = useState(null);
  const [error, setError] = useState("");
  const [elegidas, setElegidas] = useState([]); // ids, en orden de elección
  const [fecha, setFecha] = useState(hoyLocal());
  const [guardando, setGuardando] = useState(false);
  const [ampliada, setAmpliada] = useState(null);
  const [busqueda, setBusqueda] = useState("");
  const [tipo, setTipo] = useState(null);
  const [panelFecha, setPanelFecha] = useState(false);

  /* MODO EDICIÓN (21/9, pedido del dueño).
   *
   * Cuando llega una `semilla` desde Historial o el Calendario, esta
   * pantalla deja de registrar un uso nuevo y pasa a CORREGIR ese
   * registro: al guardar se manda un PATCH, no un POST. Antes el
   * «Editar» del calendario terminaba en un POST y dejaba dos registros
   * —el viejo intacto y uno nuevo—, que es lo contrario de editar.
   *
   * `conservadas` son las prendas del registro que NO están en la
   * grilla: las que borraste del armario. No se pueden mostrar ni
   * elegir, pero se mandan igual — corregir la fecha de un registro
   * viejo no puede, de paso, borrarle una prenda del pasado. */
  const [editando, setEditando] = useState(null); // { id, fecha, ocasion, ids, conservadas }
  const [ocasion, setOcasion] = useState("");
  const [ocasiones, setOcasiones] = useState([]);

  /* El perfume (22/9). Al registrar arranca con el último que usaste
   * (o el último que cargaste); al corregir, con el que tenía el
   * registro. Si la lista no llega, el frasco no aparece y todo lo
   * demás sigue igual. */
  const [perfumes, setPerfumes] = useState([]);
  const [perfume, setPerfume] = useState(null);
  /* ⚠️  SI YA SE DECIDIÓ, LA SUGERENCIA NO LA PISA. La lista de perfumes
   * y el registro a corregir llegan por caminos distintos y en cualquier
   * orden. Si el registro llega primero con «sin perfume» y después la
   * lista le pusiera el sugerido, guardar una corrección de fecha le
   * AGREGARÍA un perfume que esa noche no llevaste. */
  const perfumeDecidido = useRef(false);
  useEffect(() => {
    let vivo = true;
    api
      .listarPerfumes()
      .then((d) => {
        if (!vivo) return;
        setPerfumes(d.perfumes || []);
        if (!perfumeDecidido.current) setPerfume(perfumeSugerido(d.perfumes)?.id || null);
      })
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    // Ordenadas por uso reciente: lo que registrás es lo que usás, así
    // que lo de arriba es lo más probable.
    api
      .listarPrendas({ orden: "usadas" })
      .then((d) => setPrendas(d.prendas))
      .catch((e) => setError(tError(e)));
  }, []);

  const porId = useMemo(
    () => new Map((prendas || []).map((p) => [p.id, p])),
    [prendas]
  );

  const seleccion = elegidas.map((id) => porId.get(id)).filter(Boolean);

  const alternar = useCallback(
    (prenda) => setElegidas((antes) => alternarSeleccion(antes, prenda)),
    []
  );

  // Un outfit del calendario que llega para editarse.
  //
  // Espera a que el armario esté cargado: `prendasQueSiguenVivas` cruza
  // contra `porId` para descartar las prendas borradas, y con el mapa
  // vacío descartaría TODAS — la pantalla se abriría sin nada elegido y
  // el usuario pensaría que el botón no anduvo.
  //
  // `alUsarSemilla` limpia la semilla arriba en App. Sin eso, volver a
  // esta pestaña más tarde te volvería a cargar el mismo outfit encima
  // de lo que estuvieras armando.
  useEffect(() => {
    if (!semilla || !prendas) return;
    const vivas = prendasQueSiguenVivas(semilla, porId);
    setElegidas(vivas);
    if (semilla.id) {
      const ids = semilla.prenda_ids || [];
      const dia = diaLocalDe(semilla.usada_en) || hoyLocal();
      setEditando({
        id: semilla.id,
        fecha: dia,
        ocasion: semilla.ocasion || "",
        perfume: semilla.perfume_id || null,
        nombrePerfume: semilla.perfume?.nombre || null,
        ids,
        conservadas: ids.filter((id) => !porId.has(id)),
      });
      perfumeDecidido.current = true;
      setPerfume(semilla.perfume_id || null);
      setFecha(dia);
      setOcasion(semilla.ocasion || "");
      setPanelFecha(false);
    }
    setBusqueda("");
    setTipo(null);
    alUsarSemilla?.();
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [semilla, prendas, porId, alUsarSemilla]);

  // Las ocasiones, solo para editar: registrar a mano nunca pidió una.
  useEffect(() => {
    if (!editando) return;
    let vivo = true;
    cargarOpciones()
      .then((o) => vivo && setOcasiones(o?.categorias || []))
      .catch(() => {});
    return () => {
      vivo = false;
    };
  }, [editando]);

  function salirDeEdicion() {
    setEditando(null);
    setElegidas([]);
    setOcasion("");
    setFecha(hoyLocal());
    perfumeDecidido.current = false;
    setPerfume(perfumeSugerido(perfumes)?.id || null);
  }

  function cancelarEdicion() {
    salirDeEdicion();
    alCancelarEdicion?.();
  }

  /* Se manda SOLO lo que cambió. Mandar la fecha sin tocarla la
   * reescribiría al mediodía (`aISO`) y movería la hora original; y una
   * ocasión que ya no existe en tus listas —la borraste después— sería
   * rechazada aunque no la hayas tocado. */
  async function guardarCambios() {
    if (!editando) return;
    const ids = [...seleccion.map((p) => p.id), ...editando.conservadas];
    if (ids.length === 0) return;
    const cuerpo = {};
    const mismas =
      ids.length === editando.ids.length && ids.every((id) => editando.ids.includes(id));
    if (!mismas) cuerpo.prenda_ids = ids;
    if (fecha !== editando.fecha) cuerpo.usada_en = aISO(fecha);
    if (ocasion !== editando.ocasion) cuerpo.ocasion = ocasion || null;
    if ((perfume || null) !== editando.perfume) cuerpo.perfume_id = perfume || null;

    if (Object.keys(cuerpo).length === 0) {
      cancelarEdicion();
      return;
    }
    setGuardando(true);
    setError("");
    try {
      await api.editarOutfit({ id: editando.id, ...cuerpo });
      salirDeEdicion();
      alEditar?.();
    } catch (e) {
      setError(tError(e));
    } finally {
      setGuardando(false);
    }
  }

  async function guardar(cuando) {
    if (seleccion.length === 0) return;
    setGuardando(true);
    setError("");
    try {
      await api.registrarOutfit({
        prenda_ids: seleccion.map((p) => p.id),
        usada_en: aISO(cuando),
        perfume_id: perfume || null,
      });
      setElegidas([]);
      setPanelFecha(false);
      alGuardar?.(seleccion.length);
    } catch (e) {
      setError(tError(e));
    } finally {
      setGuardando(false);
    }
  }

  if (error && !prendas) return <div className="mensaje-error" role="alert">{error}</div>;
  if (!prendas) return <p className="tenue">{t("armador.cargando", "Cargando tu armario...")}</p>;

  const visibles = filtrarPrendas(prendas, { texto: busqueda, tipo });

  // Los tipos que existen de verdad en el armario. Ofrecer "Enterizo"
  // cuando no tenés ninguno es un botón que solo sirve para vaciar la
  // pantalla.
  const tiposPresentes = [...new Set(prendas.map((p) => p.tipo))].filter(Boolean);

  return (
    <section className={seleccion.length > 0 || editando ? "con-barra-abajo" : ""}>
      {/* ACÁ VIVÍA "REPETIR UNO DE ESTOS" (2026-08-20)
          Seis tarjetas con los últimos outfits, arriba de todo, para
          registrar de un clic algo parecido a lo ya usado.

          Se fue porque el calendario lo hace mejor. Esas seis tarjetas
          eran una ventana chiquita al historial —los últimos seis, sin
          fecha visible más que "hace 3 días"— y ocupaban la primera
          pantalla del armador todos los días para un atajo que, cuando
          se necesitaba, casi nunca estaba entre esos seis.

          En el calendario elegís CUALQUIER día de cualquier mes, lo ves
          en contexto, y desde ahí "Repetir" lo registra hoy. El atajo no
          se perdió: se mudó al lugar donde uno sabe qué está eligiendo.

          Lo que quedó de aquello es `semilla`. Desde el 21/9 es el
          registro que se va a CORREGIR («Editar» en Historial y en el
          Calendario): ver el modo edición más arriba. */}
      {error && <div className="mensaje-error" role="alert">{error}</div>}

      <div className="buscador">
        <input
          type="search"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          placeholder={t("armador.buscarPorNombre", "Buscar una prenda por nombre...")}
          aria-label={t("armador.buscarPrenda", "Buscar prenda")}
        />
        {busqueda && (
          <button
            type="button"
            className="buscador-limpiar"
            onClick={() => setBusqueda("")}
            aria-label={t("armador.limpiar", "Limpiar")}
          >
            ×
          </button>
        )}
      </div>

      {/* Filtrar por tipo ACOTA LO QUE SE VE, no lo que se puede elegir.
          Es la diferencia con los grupos por zona que había antes: esto
          organiza sin limitar. Podés filtrar "Superior", elegir dos, y
          seguir. */}
      <div className="opciones-chips">
        <button
          className={`chip ${tipo === null ? "chip-activo" : ""}`}
          onClick={() => setTipo(null)}
        >
          {t("armador.todas", "Todas")} <span className="chip-conteo">{numero(prendas.length)}</span>
        </button>
        {/* ⚠️  EL PARÁMETRO SE LLAMABA `t` Y TAPABA LA FUNCIÓN `t()`.
            Adentro de este map, `t` era el tipo de prenda, así que
            escribir una traducción acá era imposible sin darse cuenta
            de por qué. El resultado: los tipos se dibujaban en español
            —Superior, Inferior, Calzado— en los once idiomas que no lo
            son. Se renombra a `tp` y se traduce. */}
        {tiposPresentes.map((tp) => (
          <button
            key={tp}
            className={`chip ${tipo === tp ? "chip-activo" : ""}`}
            onClick={() => setTipo(tipo === tp ? null : tp)}
          >
            {tCat("tipo", tp)}{" "}
            <span className="chip-conteo">
              {numero(prendas.filter((p) => p.tipo === tp).length)}
            </span>
          </button>
        ))}
      </div>

      {visibles.length === 0 ? (
        <p className="tenue">{t("armador.nadaCoincide", "Ninguna prenda coincide.")}</p>
      ) : (
        <div className="armador-grilla">
          {visibles.map((p) => {
            const activa = elegidas.includes(p.id);
            // En qué orden se eligió. Con superposición permitida, saber
            // que la camisa va sobre la remera importa.
            const orden = elegidas.indexOf(p.id) + 1;

            return (
              <div
                key={p.id}
                className={`armador-item ${activa ? "armador-item-activo" : ""}`}
              >
                <button
                  type="button"
                  className="armador-boton"
                  onClick={() => alternar(p)}
                >
                  {fotoChica(p) ? (
                    <img loading="lazy" decoding="async" src={fotoChica(p)} alt="" className="prenda-foto" />
                  ) : (
                    <div className="prenda-foto prenda-sin-foto">
                      <span className="tenue texto-mini">{t("armador.sinFoto", "Sin foto")}</span>
                    </div>
                  )}
                  <span className="texto-mini armador-nombre">{p.nombre}</span>
                </button>

                {activa && <span className="armador-orden">{orden}</span>}

                {fotoGrande(p) && (
                  <button
                    type="button"
                    className="armador-lupa"
                    onClick={() =>
                      setAmpliada({ url: fotoGrande(p), alt: p.nombre, prenda: p })
                    }
                    aria-label={t("armador.ampliar", "Ampliar")}
                  >
                    ⤢
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* La barra de acción va ABAJO y fija. Arriba competía con la
          barra de pestañas —las dos `sticky top: 0`— y quedaba
          escondida detrás. Y en el celular el pulgar llega abajo. */}
      {editando ? (
        <div className="barra-armador">
          <div className="barra-armador-contenido">
            <p className="texto-chico" style={{ margin: 0 }}>
              <strong>
                {t("armador.editandoDel", "Editando el registro del {fecha}", {
                  fecha: fechaEnIdioma(new Date(aISO(editando.fecha)), {
                    day: "numeric",
                    month: "long",
                  }),
                })}
              </strong>
            </p>
            {seleccion.length > 0 && (
              <div className="armador-elegidas">
                {seleccion.map((p, i) => (
                  <button
                    key={p.id}
                    className="armador-chip-prenda"
                    onClick={() => alternar(p)}
                    title={t("armador.sacarPrenda", "Sacar {nombre}", { nombre: p.nombre })}
                  >
                    <span className="armador-chip-orden">{i + 1}</span>
                    {fotoChica(p) && <img loading="lazy" decoding="async" src={fotoChica(p)} alt="" />}
                    <span>{p.nombre}</span>
                    <span className="armador-quitar">×</span>
                  </button>
                ))}
              </div>
            )}
            {editando.conservadas.length > 0 && (
              <p className="texto-mini tenue" style={{ margin: 0 }}>
                {t("armador.conservadas", "Prendas que ya no están en tu armario y se mantienen: {n}", {
                  n: editando.conservadas.length,
                })}
              </p>
            )}
            {seleccion.length + editando.conservadas.length === 0 && (
              <p className="texto-mini tenue" style={{ margin: 0 }}>
                {t("armador.elegiUna", "Elige al menos una prenda.")}
              </p>
            )}
            <div className="fila-fecha">
              <input
                type="date"
                value={fecha}
                max={hoyLocal()}
                onChange={(e) => setFecha(e.target.value)}
                aria-label={t("armador.fecha", "Fecha")}
              />
              {ocasiones.length > 0 && (
                <label className={`selector ${ocasion ? "selector-activo" : ""}`}>
                  <span className="selector-etiqueta">{t("armador.ocasion", "Ocasión")}</span>
                  <select value={ocasion} onChange={(e) => setOcasion(e.target.value)}>
                    <option value="">{t("armador.sinOcasion", "Sin ocasión")}</option>
                    {/* Si la ocasión del registro ya no está en tus listas,
                        igual se muestra: si no, el select la perdería al
                        abrirse y parecería que la borraste. */}
                    {[...ocasiones, ...(ocasion && !ocasiones.includes(ocasion) ? [ocasion] : [])].map(
                      (v) => (
                        <option key={v} value={v}>
                          {tCat("ocasion", v)}
                        </option>
                      )
                    )}
                  </select>
                </label>
              )}
            </div>
            {/* El frasco en su propio renglón: dentro de la fila de fecha
                y ocasión quedaba afuera de la pantalla en el celular
                (captura a 390px, 22/9). */}
            <FrascoBoton
              perfumes={perfumes}
              valor={perfume}
              onCambiar={setPerfume}
              nombreFuera={editando.nombrePerfume}
            />
            <div className="barra-armador-botones">
              <button
                className="boton-primario"
                onClick={guardarCambios}
                disabled={guardando || !fecha || seleccion.length + editando.conservadas.length === 0}
              >
                {guardando ? t("armador.guardando", "Guardando...") : t("armador.guardarCambios", "Guardar cambios")}
              </button>
              <button className="boton-texto" onClick={cancelarEdicion} disabled={guardando}>
                {t("armador.cancelar", "Cancelar")}
              </button>
            </div>
          </div>
        </div>
      ) : seleccion.length > 0 && (
        <div className="barra-armador">
          <div className="barra-armador-contenido">
            <div className="armador-elegidas">
              {seleccion.map((p, i) => (
                <button
                  key={p.id}
                  className="armador-chip-prenda"
                  onClick={() => alternar(p)}
                  title={t("armador.sacarPrenda", "Sacar {nombre}", { nombre: p.nombre })}
                >
                  <span className="armador-chip-orden">{i + 1}</span>
                  {fotoChica(p) && <img loading="lazy" decoding="async" src={fotoChica(p)} alt="" />}
                  <span>{p.nombre}</span>
                  <span className="armador-quitar">×</span>
                </button>
              ))}
            </div>

            {panelFecha ? (
              <div className="fila-fecha">
                <input
                  type="date"
                  value={fecha}
                  max={hoyLocal()}
                  onChange={(e) => setFecha(e.target.value)}
                />
                <button
                  className="boton-secundario"
                  onClick={() => guardar(fecha)}
                  disabled={guardando}
                >
                  {guardando ? "..." : t("armador.registrar", "Registrar")}
                </button>
                <button className="boton-texto" onClick={() => setPanelFecha(false)}>
                  {t("armador.volver", "Volver")}
                </button>
              </div>
            ) : (
              <>
              {/* El frasco va antes de los botones que registran: se
                  elige el perfume y después «Me lo puse». */}
              <FrascoBoton perfumes={perfumes} valor={perfume} onCambiar={setPerfume} />
              <div className="barra-armador-botones">
                <button
                  className="boton-primario"
                  onClick={() => guardar(hoyLocal())}
                  disabled={guardando}
                >
                  {guardando
                    ? t("armador.guardando", "Guardando...")
                    : t("armador.meLoPuseHoy", "Me lo puse hoy ({n})", { n: seleccion.length })}
                </button>
                <button
                  className="boton-secundario"
                  onClick={() => guardar(hace(1))}
                  disabled={guardando}
                >
                  {t("armador.ayer", "Ayer")}
                </button>
                <button
                  className="boton-secundario"
                  onClick={() => setPanelFecha(true)}
                  disabled={guardando}
                  title={t("armador.otraFecha", "Otra fecha")}
                  aria-label={t("armador.elegirFecha", "Elegir otra fecha")}
                >
                  📅
                </button>
                <button
                  className="boton-texto boton-angosto"
                  onClick={() => setElegidas([])}
                >
                  {t("armador.vaciar", "Vaciar")}
                </button>
              </div>
              </>
            )}
          </div>
        </div>
      )}

      <Visor
        url={ampliada?.url}
        alt={ampliada?.alt}
        prenda={ampliada?.prenda}
        onCerrar={() => setAmpliada(null)}
      />
    </section>
  );
}
